import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Star, Users, Sparkles, ArrowRight, Eye, EyeOff, CheckCircle, IndianRupee, 
  Zap, TrendingUp, BadgeCheck, Gift, Clock, X, Play, Instagram, Youtube, 
  Heart, MapPin, Filter, Globe, ChevronDown, Calculator, Search, Camera, 
  Film, Music, Mic, PenTool, Smartphone, Crown, Shield, Send, Image,
  Clapperboard, Gamepad2
} from "lucide-react";
import { useAuth, API } from "@/App";
import { t as tr, setLang as persistLang, getLang } from "@/i18n/translations";
import SipStyleFilter, {
  FOLLOWERS_MIN,
  FOLLOWERS_MAX,
  BUDGET_MIN,
  BUDGET_MAX,
} from "@/components/SipStyleFilter";

const NewLandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user, loading: authLoading } = useAuth();
  
  // Auth Modal States
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("signup"); // signup, login
  const [authFlow, setAuthFlow] = useState("talent"); // talent, brand
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Forgot Password States
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1=email, 2=OTP, 3=new password
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotShowPassword, setForgotShowPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const resetForgotState = () => {
    setForgotStep(1);
    setForgotEmail("");
    setForgotOtp("");
    setForgotNewPassword("");
    setForgotShowPassword(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (forgotStep === 1) {
      if (!forgotEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
        toast.error("Please enter a valid email");
        return;
      }
      setForgotLoading(true);
      try {
        await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
        toast.success("If this email is registered, an OTP has been sent.");
        setForgotStep(2);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to send OTP");
      } finally {
        setForgotLoading(false);
      }
    } else if (forgotStep === 2) {
      if (!forgotOtp || forgotOtp.length !== 6) {
        toast.error("Please enter the 6-digit OTP");
        return;
      }
      setForgotLoading(true);
      try {
        await axios.post(`${API}/auth/verify-otp`, { email: forgotEmail, otp: forgotOtp });
        toast.success("OTP verified — set a new password");
        setForgotStep(3);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Invalid or expired OTP");
      } finally {
        setForgotLoading(false);
      }
    } else if (forgotStep === 3) {
      if (!forgotNewPassword || forgotNewPassword.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }
      setForgotLoading(true);
      try {
        await axios.post(`${API}/auth/reset-password`, {
          email: forgotEmail,
          otp: forgotOtp,
          new_password: forgotNewPassword,
        });
        toast.success("Password reset! Please log in with your new password.");
        setShowForgotModal(false);
        resetForgotState();
        setShowAuthModal(true);
        setAuthMode("login");
        setFormData((p) => ({ ...p, email: forgotEmail, password: "" }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to reset password");
      } finally {
        setForgotLoading(false);
      }
    }
  };

  
  // Form Data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    company_name: "",
    website_url: "",
    social_media_url: ""
  });
  
  // Talent Data
  const [talents, setTalents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pricing, setPricing] = useState({});
  const [loadingTalents, setLoadingTalents] = useState(true);
  
  // Filter States
  const [filters, setFilters] = useState({
    category: "all",
    minFollowers: FOLLOWERS_MIN,
    maxFollowers: FOLLOWERS_MAX,
    minBudget: BUDGET_MIN,
    maxBudget: BUDGET_MAX,
    radiusKm: null,
    language: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  // Geolocation
  const [centerLocation, setCenterLocation] = useState(null);
  const [detectingLocation, setDetectingLocation] = useState(false);

  const requestLocation = () => {
    if (!navigator.geolocation) return;
    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        let label = "your location";
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`
          );
          const data = await r.json();
          label =
            data?.address?.city ||
            data?.address?.town ||
            data?.address?.village ||
            data?.address?.state ||
            "your location";
        } catch (_) {}
        setCenterLocation({ lat: latitude, lng: longitude, label });
        setDetectingLocation(false);
      },
      () => {
        setDetectingLocation(false);
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };
  
  // Calculator State
  const [calcFollowers, setCalcFollowers] = useState(100);
  const [calcCategory, setCalcCategory] = useState("influencers");
  
  // Live Ticker
  const [tickerIndex, setTickerIndex] = useState(0);
  const tickerMessages = [
    { text: "A creator just got hired by a Fashion Brand", icon: "sparkles" },
    { text: "New star joined from Mumbai", icon: "user" },
    { text: "Model booked for product shoot", icon: "camera" },
    { text: "Voice artist completed ad project", icon: "mic" },
  ];
  
  // Video Popup
  const [videoPopup, setVideoPopup] = useState({ show: false, url: "", title: "" });
  
  // Language Toggle
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "hi", name: "हिंदी", flag: "🇮🇳" },
    { code: "mr", name: "मराठी", flag: "🇮🇳" },
    { code: "ta", name: "தமிழ்", flag: "🇮🇳" },
    { code: "te", name: "తెలుగు", flag: "🇮🇳" },
    { code: "gu", name: "ગુજરાતી", flag: "🇮🇳" },
  ];
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = getLang();
    return languages.find((l) => l.code === saved) || languages[0];
  });

  // Translation helper bound to current language (re-renders on change)
  const t = (key) => tr(key, currentLanguage.code);
  
  // Dynamic Content
  const [testimonials, setTestimonials] = useState([]);
  
  // Fetch data on mount
  useEffect(() => {
    fetchCategories();
    fetchTalents();
    fetchTestimonials();
    
    // Ticker rotation
    const tickerInterval = setInterval(() => {
      setTickerIndex(prev => (prev + 1) % tickerMessages.length);
    }, 4000);
    
    return () => clearInterval(tickerInterval);
  }, []);
  
  // Handle URL params (for redirects)
  useEffect(() => {
    const signup = searchParams.get('signup');
    const login = searchParams.get('login');
    const ref = searchParams.get('ref');
    
    if (ref) {
      localStorage.setItem('foundstar_referral', ref);
    }
    
    if (signup === 'talent') {
      setAuthFlow('talent');
      setAuthMode('signup');
      setShowAuthModal(true);
    } else if (signup === 'brand') {
      setAuthFlow('brand');
      setAuthMode('signup');
      setShowAuthModal(true);
    } else if (login) {
      setAuthFlow(login);
      setAuthMode('login');
      setShowAuthModal(true);
    }
  }, [searchParams]);
  
  // Redirect if logged in
  useEffect(() => {
    if (!authLoading && user) {
      if (user.user_type === "brand") {
        navigate("/brand/home", { replace: true });
      } else {
        navigate("/influencer/home", { replace: true });
      }
    }
  }, [authLoading, user, navigate]);

  // Programmatic language switch — uses local i18n dictionary (no external script)
  const translatePage = (langCode) => {
    const lang = languages.find((l) => l.code === langCode);
    if (!lang) return;
    setCurrentLanguage(lang);
    persistLang(langCode);
    setShowLanguageMenu(false);
    if (langCode !== "en") {
      toast.success(`🌐 ${lang.name}`);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/talent/categories`);
      setCategories(response.data.categories || []);
      setPricing(response.data.pricing || {});
    } catch (error) {
      console.error("Failed to fetch categories");
      // Fallback categories
      setCategories([
        { id: "influencers", name: "Influencers", color: "#E91E63", icon: "Instagram", description: "Instagram, YouTube, Facebook" },
        { id: "models", name: "Models", color: "#FF4D6D", icon: "Camera", description: "Fashion, Product, Jewelry" },
        { id: "actors", name: "Actors", color: "#7B2FBE", icon: "Film", description: "Ad films, Reels, Corporate" },
        { id: "dancers", name: "Dancers", color: "#FF6B00", icon: "Music", description: "Events, Reels, Shows" },
        { id: "photographers", name: "Photographers", color: "#00A86B", icon: "Camera", description: "Product shoots, Events" },
        { id: "content_writers", name: "Content Writers", color: "#FFD700", icon: "PenTool", description: "Captions, Blogs, Scripts" },
        { id: "voice_artists", name: "Voice Artists", color: "#1A73E8", icon: "Mic", description: "Ads, Podcasts, Dubbing" },
      ]);
    }
  };
  
  const fetchTalents = async () => {
    setLoadingTalents(true);
    try {
      const params = new URLSearchParams();
      if (filters.category !== "all") params.append("category", filters.category);
      if (filters.minFollowers > FOLLOWERS_MIN) params.append("min_followers", String(filters.minFollowers));
      if (filters.maxFollowers < FOLLOWERS_MAX) params.append("max_followers", String(filters.maxFollowers));
      if (filters.minBudget > BUDGET_MIN) params.append("min_rate", String(filters.minBudget));
      if (filters.maxBudget < BUDGET_MAX) params.append("max_rate", String(filters.maxBudget));
      if (centerLocation && filters.radiusKm) {
        params.append("near_lat", String(centerLocation.lat));
        params.append("near_lng", String(centerLocation.lng));
        params.append("radius_km", String(filters.radiusKm));
      }
      
      const response = await axios.get(`${API}/talents?${params.toString()}`);
      setTalents(response.data.talents || []);
    } catch (error) {
      console.error("Failed to fetch talents");
    } finally {
      setLoadingTalents(false);
    }
  };
  
  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/landing/testimonial-videos`);
      setTestimonials(response.data.videos || []);
    } catch (error) {
      console.error("Failed to fetch testimonials");
    }
  };

  // Helper to get proper photo URL - use ONLY picture field, NOT photos array (photos = portfolio)
  const getPhotoUrl = (talent) => {
    const photo = talent.picture; // Only use picture, not photos (portfolio)
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${API}/files/${photo}`;
  };
  
  // Apply filters
  useEffect(() => {
    fetchTalents();
  }, [filters, centerLocation]);
  
  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (authMode === "login") {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        login(response.data, response.data.token);
        toast.success("Welcome back!");
        setShowAuthModal(false);
        
        // Redirect based on user type
        if (response.data.user_type === "brand") {
          navigate("/brand/home");
        } else {
          navigate("/influencer/home");
        }
      } else {
        // Register
        const registerData = {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          user_type: authFlow === "brand" ? "brand" : "influencer" // Talents register as "influencer" type
        };
        
        if (authFlow === "brand") {
          registerData.company_name = formData.company_name;
          registerData.website_url = formData.website_url || null;
          registerData.social_media_url = formData.social_media_url || null;
        }
        
        const response = await axios.post(`${API}/auth/register`, registerData);
        login(response.data, response.data.token);
        
        // Apply referral if exists
        const referralCode = localStorage.getItem('foundstar_referral');
        if (referralCode) {
          try {
            await axios.post(`${API}/referral/apply`, { code: referralCode }, {
              headers: { Authorization: `Bearer ${response.data.token}` }
            });
            localStorage.removeItem('foundstar_referral');
          } catch (e) {}
        }
        
        toast.success("Account created successfully!");
        setShowAuthModal(false);
        
        // Redirect
        if (authFlow === "brand") {
          navigate("/brand/home");
        } else {
          // Go to Star home page
          navigate("/influencer/home");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };
  
  const getCategoryIcon = (iconName) => {
    const icons = {
      Camera: Camera,
      Film: Film,
      Music: Music,
      Mic: Mic,
      PenTool: PenTool,
      Instagram: Smartphone,
      Clapperboard: Clapperboard,
      Gamepad2: Gamepad2,
    };
    return icons[iconName] || Camera;
  };
  
  const formatRate = (rate) => {
    if (rate >= 100000) return `₹${(rate / 100000).toFixed(1)}L`;
    if (rate >= 1000) return `₹${(rate / 1000).toFixed(0)}K`;
    return `₹${rate}`;
  };
  
  const calculateEarnings = () => {
    // Base rates per category
    const baseRates = {
      models: { perHour: 1500, perProject: 8000 },
      actors: { perHour: 2000, perProject: 15000 },
      dancers: { perHour: 1000, perProject: 5000 },
      voice_artists: { perHour: 1500, perProject: 3000 },
      photographers: { perHour: 2000, perProject: 10000 },
      content_writers: { perHour: 500, perProject: 2000 }
    };
    
    if (calcCategory === "influencers") {
      // 100-1000 followers = ₹49/post (fixed)
      // Above 1000, scale proportionally
      let perPost;
      if (calcFollowers <= 1000) {
        perPost = 49; // Fixed ₹49 for 100-1000 followers
      } else {
        // Above 1000: ₹49 base + proportional increase
        // Rate: ₹0.049 per follower above 1000
        perPost = 49 + Math.round((calcFollowers - 1000) * 0.049);
      }
      const monthly = perPost * 20; // 20 posts per month
      return { perPost, monthly };
    }
    
    const rate = baseRates[calcCategory];
    return { perHour: rate?.perHour || 1000, perProject: rate?.perProject || 5000 };
  };
  
  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF69B4]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl text-[#1E293B]">FOUNDSTAR</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-6">
            <a href="#talents" className="text-sm text-gray-600 hover:text-[#FF69B4] transition-colors">{t("nav_find_stars")}</a>
            <a href="#categories" className="text-sm text-gray-600 hover:text-[#FF69B4] transition-colors">{t("nav_categories")}</a>
            <a href="#calculator" className="text-sm text-gray-600 hover:text-[#FF69B4] transition-colors">{t("nav_earnings")}</a>
          </nav>
          
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <div className="relative">
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 hover:border-[#FF69B4] transition-colors text-sm"
              >
                <Globe className="w-4 h-4 text-gray-500" />
                <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.name}</span>
                <span className="sm:hidden">{currentLanguage.flag}</span>
                <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${showLanguageMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showLanguageMenu && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-gray-100 py-2 min-w-[140px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => translatePage(lang.code)}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-pink-50 flex items-center gap-2 ${
                        currentLanguage.code === lang.code ? 'text-[#FF69B4] bg-pink-50' : 'text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => { setAuthMode('login'); setAuthFlow('talent'); setShowAuthModal(true); }}
              className="text-gray-600 hover:text-[#FF69B4]"
            >
              {t("btn_login")}
            </Button>
            <Button 
              onClick={() => { setAuthMode('signup'); setAuthFlow('talent'); setShowAuthModal(true); }}
              className="bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] hover:opacity-90 text-white rounded-full px-5"
            >
              {t("btn_join_free")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF69B4] via-[#FF69B4]/80 to-[#7B2FBE]"></div>
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}}></div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            {/* Urgency Tag - Green for trust/safety/go */}
            <div className="inline-flex items-center gap-2 bg-[#00A86B] text-white px-4 py-2 rounded-full text-sm font-medium mb-6 animate-pulse">
              <Zap className="w-4 h-4" />
              {t("hero_tag")}
            </div>
            
            {/* Headline */}
            <h1 className="font-outfit font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-4 leading-tight">
              {t("hero_title_1")}<br />
              <span className="text-yellow-300">{t("hero_title_2")}</span>
            </h1>
            
            {/* Sub-headline */}
            <p className="text-white/90 text-lg md:text-xl mb-8">
              {t("hero_subtitle")}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={() => { setAuthMode('signup'); setAuthFlow('brand'); setShowAuthModal(true); }}
                className="bg-white text-[#FF69B4] hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all border-2 border-[#1A73E8]"
                data-testid="find-talent-btn"
              >
                {t("hero_cta_find")}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => { setAuthMode('signup'); setAuthFlow('talent'); setShowAuthModal(true); }}
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
                data-testid="join-free-btn"
              >
                {t("hero_cta_be")}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            {/* Data Safety Assurance */}
            <p className="text-white/70 text-xs mt-3">{t("hero_safe")}</p>
            
            {/* Trust Badges - Blue icons for trust */}
            <div className="flex items-center justify-center gap-6 mt-8 text-white/90 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#1A73E8]" />
                <span>{t("trust_escrow")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#1A73E8]" />
                <span>{t("trust_verified")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-[#1A73E8]" />
                <span>{t("trust_free")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section id="categories" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-outfit font-bold text-3xl text-[#1E293B] mb-2">{t("section_browse")}</h2>
            <p className="text-gray-500">{t("section_browse_sub")}</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {categories.map((cat) => {
              const IconComponent = getCategoryIcon(cat.icon);
              return (
                <button
                  key={cat.id}
                  onClick={() => setFilters({ ...filters, category: cat.id })}
                  className={`group p-4 rounded-2xl border-2 transition-all hover:shadow-lg ${
                    filters.category === cat.id 
                      ? 'border-[#FF69B4] bg-pink-50' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${cat.color}20` }}
                  >
                    <IconComponent className="w-6 h-6" style={{ color: cat.color }} />
                  </div>
                  <p className="font-medium text-sm text-[#1E293B]">{cat.name}</p>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{cat.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Talents Section */}
      <section id="talents" className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* SIP-Calculator-style Filter — Category, Area, Followers, Budget */}
          <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-outfit font-bold text-2xl text-[#1E293B]">
                {filters.category === "all" ? "Top 20 Stars" : categories.find(c => c.id === filters.category)?.name}
              </h2>
              <p className="text-gray-500">{Math.min(talents.length, 20)} {t("section_stars_available")}</p>
            </div>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`border-gray-200 rounded-full ${showFilters ? "bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] text-white border-transparent" : ""}`}
              data-testid="landing-toggle-filters"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? "Hide Filters" : t("filters_btn")}
            </Button>
          </div>

          {/* SIP Style Filter Card */}
          {showFilters && (
            <div className="mb-8" data-testid="landing-sip-filter">
              <SipStyleFilter
                categories={categories.map(c => ({
                  id: c.id,
                  name: c.name,
                  color: c.color,
                  icon: getCategoryIcon(c.icon),
                }))}
                selectedCategory={filters.category}
                onCategoryChange={(id) => setFilters({ ...filters, category: id })}
                filters={{
                  minFollowers: filters.minFollowers,
                  maxFollowers: filters.maxFollowers,
                  minBudget: filters.minBudget,
                  maxBudget: filters.maxBudget,
                  radiusKm: filters.radiusKm,
                }}
                onApply={(f) => {
                  setFilters({
                    ...filters,
                    minFollowers: f.minFollowers,
                    maxFollowers: f.maxFollowers,
                    minBudget: f.minBudget,
                    maxBudget: f.maxBudget,
                    radiusKm: f.radiusKm,
                  });
                }}
                onReset={() => {
                  setFilters({
                    ...filters,
                    minFollowers: FOLLOWERS_MIN,
                    maxFollowers: FOLLOWERS_MAX,
                    minBudget: BUDGET_MIN,
                    maxBudget: BUDGET_MAX,
                    radiusKm: null,
                  });
                }}
                resultCount={talents.length}
                title="Find your perfect star"
                variant="dark"
                centerLocation={centerLocation}
                onRequestLocation={requestLocation}
                detectingLocation={detectingLocation}
              />
            </div>
          )}
          
          {/* Talent Grid */}
          {loadingTalents ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#FF69B4]"></div>
            </div>
          ) : talents.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No talents found</h3>
              <p className="text-gray-400 mb-6">Be the first to join this category!</p>
              <Button 
                onClick={() => { setAuthMode('signup'); setAuthFlow('talent'); setShowAuthModal(true); }}
                className="bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] text-white rounded-full"
              >
                I'm Creator
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {talents.slice(0, 20).map((talent) => (
                <Card 
                  key={talent.user_id} 
                  className="group overflow-hidden border-2 border-transparent hover:border-[#FF69B4] transition-all hover:shadow-xl relative"
                >
                  <CardContent className="p-0">
                    {/* Full Card Background Image - Blurred */}
                    {getPhotoUrl(talent) && (
                      <div className="absolute inset-0 z-0">
                        <img 
                          src={getPhotoUrl(talent)} 
                          alt=""
                          className="w-full h-full object-cover blur-xl opacity-30 scale-110"
                        />
                      </div>
                    )}
                    
                    {/* Image Section */}
                    <div className="relative h-56 bg-gradient-to-br from-pink-100 to-purple-100 z-10">
                      {getPhotoUrl(talent) ? (
                        <img 
                          src={getPhotoUrl(talent)} 
                          alt={talent.name}
                          className="w-full h-full object-cover object-center"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="w-16 h-16 text-gray-300" />
                        </div>
                      )}
                      
                      {/* Badges */}
                      <div className="absolute top-3 left-3 flex gap-2">
                        {talent.is_verified && (
                          <Badge className="bg-[#7B2FBE] text-white text-xs">
                            <BadgeCheck className="w-3 h-3 mr-1" />
                            Pro Creator
                          </Badge>
                        )}
                        {talent.is_featured && (
                          <Badge className="bg-[#FFD700] text-[#1E293B] text-xs">
                            <Crown className="w-3 h-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>
                      
                      {/* Category Badge */}
                      <div className="absolute bottom-3 left-3">
                        <Badge 
                          style={{ backgroundColor: categories.find(c => c.id === talent.category)?.color || "#FF69B4" }}
                          className="text-white text-xs"
                        >
                          {categories.find(c => c.id === talent.category)?.name || talent.category}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Info Section with blurred profile background */}
                    <div className="p-4 relative z-10">
                      {/* Blurred background */}
                      {getPhotoUrl(talent) && (
                        <div className="absolute inset-0 -z-10 overflow-hidden rounded-b-lg">
                          <img 
                            src={getPhotoUrl(talent)} 
                            alt=""
                            className="w-full h-full object-cover blur-2xl opacity-40 scale-150"
                          />
                          <div className="absolute inset-0 bg-white/60"></div>
                        </div>
                      )}
                      {/* Name & Location */}
                      <div className="mb-3">
                        <h3 className="font-semibold text-[#1E293B] text-lg">{talent.name}</h3>
                        {talent.city && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {talent.city}
                          </p>
                        )}
                      </div>
                      
                      {/* Stats Grid - Followers, Trust, Deals */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="bg-[#FDE8EF] rounded-2xl py-4 px-2 text-center">
                          <p className="font-bold text-[#1E293B] text-xl">
                            {talent.followers_count >= 1000000 
                              ? `${(talent.followers_count / 1000000).toFixed(1)}M`
                              : talent.followers_count >= 1000 
                                ? `${Math.round(talent.followers_count / 1000)}K`
                                : talent.followers_count || 0}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">Followers</p>
                        </div>
                        <div className="bg-[#FFF8E1] rounded-2xl py-4 px-2 text-center">
                          <p className="font-bold text-[#1E293B] text-xl flex items-center justify-center gap-1">
                            {talent.trust_score || talent.rating ? (
                              <>{(talent.trust_score || talent.rating)?.toFixed(1)}</>
                            ) : (
                              <><Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />New</>
                            )}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">Trust</p>
                        </div>
                        <div className="bg-[#E3F2FD] rounded-2xl py-4 px-2 text-center">
                          <p className="font-bold text-[#1E293B] text-xl">{talent.bookings_completed || talent.closed_deals || 0}</p>
                          <p className="text-xs text-gray-400 mt-1">Deals</p>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="grid grid-cols-3 gap-2">
                        <Button 
                          variant="outline"
                          onClick={() => { setAuthMode('signup'); setAuthFlow('brand'); setShowAuthModal(true); }}
                          className="rounded-xl text-xs h-10 border-gray-200"
                          size="sm"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => { setAuthMode('signup'); setAuthFlow('brand'); setShowAuthModal(true); }}
                          className="rounded-xl text-xs h-10 border-gray-200"
                          size="sm"
                        >
                          <Image className="w-3 h-3 mr-1" />
                          Work
                        </Button>
                        <Button 
                          onClick={() => { setAuthMode('signup'); setAuthFlow('brand'); setShowAuthModal(true); }}
                          className="bg-[#FF69B4] hover:bg-[#FF69B4]/90 text-white rounded-xl text-xs h-10"
                          size="sm"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Hire Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* View All Button - Opens Brand Signup */}
          {talents.length >= 8 && (
            <div className="text-center mt-10">
              <Button 
                onClick={() => { setAuthMode('signup'); setAuthFlow('brand'); setShowAuthModal(true); }}
                variant="outline" 
                className="border-[#FF69B4] text-[#FF69B4] hover:bg-pink-50 rounded-full px-8"
              >
                View All Stars
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-gray-400 text-sm mt-2">Sign up as Brand to see all Stars</p>
            </div>
          )}
        </div>
      </section>

      {/* Earnings Calculator */}
      <section id="calculator" className="py-16 bg-gradient-to-r from-pink-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-outfit font-bold text-3xl text-[#1E293B] mb-2">Earning Calculator</h2>
            <p className="text-gray-500">See how much you could earn as a talent</p>
          </div>
          
          <Card className="shadow-xl">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <Label className="text-sm font-medium mb-4 block">Select Category</Label>
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {categories.slice(0, 4).map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setCalcCategory(cat.id)}
                        className={`p-3 rounded-xl border-2 text-sm transition-all ${
                          calcCategory === cat.id ? 'border-[#FF69B4] bg-pink-50' : 'border-gray-200'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                  
                  {calcCategory === "influencers" && (
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Your Followers</Label>
                      <Slider
                        value={[calcFollowers]}
                        onValueChange={([v]) => setCalcFollowers(v)}
                        min={100}
                        max={1000000}
                        step={100}
                        className="mb-2"
                      />
                      <p className="text-center text-lg font-bold text-[#FF69B4]">
                        {calcFollowers >= 1000000 ? `${(calcFollowers/1000000).toFixed(1)}M` : calcFollowers >= 1000 ? `${(calcFollowers/1000).toFixed(1)}K` : calcFollowers} followers
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="bg-gradient-to-br from-[#FF69B4] to-[#7B2FBE] rounded-2xl p-6 text-white">
                  <h3 className="font-semibold mb-4">Your Potential Earnings</h3>
                  
                  {calcCategory === "influencers" ? (
                    <div className="space-y-4">
                      <div className="bg-white/20 rounded-xl p-4">
                        <p className="text-white/80 text-sm">Per Post</p>
                        <p className="text-3xl font-bold">₹{calculateEarnings().perPost?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white/20 rounded-xl p-4">
                        <p className="text-white/80 text-sm">Monthly (20 posts)</p>
                        <p className="text-3xl font-bold">₹{calculateEarnings().monthly?.toLocaleString()}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-white/20 rounded-xl p-4">
                        <p className="text-white/80 text-sm">Per Collab</p>
                        <p className="text-3xl font-bold">₹{calculateEarnings().perProject?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white/20 rounded-xl p-4">
                        <p className="text-white/80 text-sm">Monthly (5 collabs)</p>
                        <p className="text-3xl font-bold">₹{(calculateEarnings().perProject * 5)?.toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    onClick={() => { setAuthMode('signup'); setAuthFlow('talent'); setShowAuthModal(true); }}
                    className="w-full mt-4 bg-white text-[#FF69B4] hover:bg-white/90 rounded-full"
                  >
                    Start Earning Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="font-outfit font-bold text-3xl text-[#1E293B] mb-2">Success Stories</h2>
              <p className="text-gray-500">Hear from our talented creators</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((video, i) => (
                <Card key={i} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setVideoPopup({ show: true, url: video.url, title: video.title })}>
                  <div className="relative h-48 bg-gray-100">
                    {video.thumbnail ? (
                      <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-100 to-purple-100">
                        <Play className="w-12 h-12 text-[#FF69B4]" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-[#FF69B4] ml-1" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-medium text-[#1E293B]">{video.title || "Success Story"}</h3>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-outfit font-bold text-3xl md:text-4xl text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Join thousands of talented creators and brands on India's fastest growing platform
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => { setAuthMode('signup'); setAuthFlow('brand'); setShowAuthModal(true); }}
              className="bg-white text-[#FF69B4] hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold"
            >
              I'm a Brand
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              onClick={() => { setAuthMode('signup'); setAuthFlow('talent'); setShowAuthModal(true); }}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
            >
              I'm a Creator
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          
          <p className="text-white/60 text-sm mt-6">
            Free to join • No hidden fees • Secure payments
          </p>
        </div>
      </section>

      {/* Live Ticker */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#1E293B] text-white py-2 z-40">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2">
          <span className="inline-flex w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-sm">LIVE: {tickerMessages[tickerIndex].text}</span>
        </div>
      </div>

      {/* Footer Spacer */}
      <div className="h-10"></div>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="sm:max-w-md">
          <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] rounded-t-lg"></div>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-center">
              {authMode === "login" ? "Welcome Back" : authFlow === "brand" ? "I'm Brand" : "I'm Creator"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAuth} className="space-y-4">
            {/* Toggle Login/Signup */}
            <div className="flex rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setAuthMode('signup')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                  authMode === 'signup' ? 'bg-white shadow text-[#FF69B4]' : 'text-gray-500'
                }`}
              >
                Sign Up
              </button>
              <button
                type="button"
                onClick={() => setAuthMode('login')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                  authMode === 'login' ? 'bg-white shadow text-[#FF69B4]' : 'text-gray-500'
                }`}
              >
                Log In
              </button>
            </div>
            
            {/* Toggle Talent/Brand (for signup only) */}
            {authMode === "signup" && (
              <div className="flex rounded-xl border-2 border-gray-200 p-1">
                <button
                  type="button"
                  onClick={() => setAuthFlow('talent')}
                  className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    authFlow === 'talent' ? 'bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] text-white' : 'text-gray-500'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  I'm Creator
                </button>
                <button
                  type="button"
                  onClick={() => setAuthFlow('brand')}
                  className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    authFlow === 'brand' ? 'bg-gradient-to-r from-[#87CEEB] to-[#4169E1] text-white' : 'text-gray-500'
                  }`}
                >
                  <Search className="w-4 h-4" />
                  I'm Brand
                </button>
              </div>
            )}
            
            {authMode === "signup" && (
              <div>
                <Label className="text-sm">Name</Label>
                <Input
                  name="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            )}
            
            {authMode === "signup" && authFlow === "brand" && (
              <div>
                <Label className="text-sm">Company Name</Label>
                <Input
                  name="company_name"
                  placeholder="Your company"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            )}
            
            {authMode === "signup" && authFlow === "brand" && (
              <div>
                <Label className="text-sm">Website URL <span className="text-gray-400">(optional)</span></Label>
                <Input
                  name="website_url"
                  type="url"
                  placeholder="https://yourcompany.com"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  className="mt-1"
                />
              </div>
            )}
            
            {authMode === "signup" && authFlow === "brand" && (
              <div>
                <Label className="text-sm">Social Media / Instagram URL <span className="text-red-500">*</span></Label>
                <Input
                  name="social_media_url"
                  type="url"
                  placeholder="https://instagram.com/yourcompany"
                  value={formData.social_media_url}
                  onChange={(e) => setFormData({ ...formData, social_media_url: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-400 mt-1">Either Website or Social Media URL required</p>
              </div>
            )}
            
            <div>
              <Label className="text-sm">Email</Label>
              <Input
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-sm">Password</Label>
              <div className="relative mt-1">
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {authMode === "login" && (
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAuthModal(false);
                      resetForgotState();
                      setForgotEmail(formData.email || "");
                      setShowForgotModal(true);
                    }}
                    className="text-xs font-medium text-[#FF69B4] hover:text-[#7B2FBE] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
            </div>
            
            {authMode === "signup" && authFlow === "talent" && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                <p className="text-green-700 font-medium">Free to Join!</p>
                <p className="text-green-600 text-sm">No fees, start earning immediately</p>
              </div>
            )}
            
            <Button
              type="submit"
              disabled={loading}
              className={`w-full py-6 rounded-full text-lg font-semibold ${
                authFlow === "brand" 
                  ? 'bg-gradient-to-r from-[#87CEEB] to-[#4169E1]' 
                  : 'bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE]'
              } text-white hover:opacity-90`}
            >
              {loading ? "Please wait..." : authMode === "login" ? "Log In" : "Create Account"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotModal} onOpenChange={(o) => { setShowForgotModal(o); if (!o) resetForgotState(); }}>
        <DialogContent className="sm:max-w-md">
          <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] rounded-t-lg"></div>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-center">
              {forgotStep === 1 && "Reset Password"}
              {forgotStep === 2 && "Enter OTP"}
              {forgotStep === 3 && "Set New Password"}
            </DialogTitle>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center justify-center gap-2 -mt-1">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1.5 rounded-full transition-all ${
                  s === forgotStep ? "w-8 bg-[#FF69B4]" : s < forgotStep ? "w-6 bg-[#7B2FBE]" : "w-6 bg-gray-200"
                }`}
              />
            ))}
          </div>

          <form onSubmit={handleForgotSubmit} className="space-y-4 mt-2">
            {forgotStep === 1 && (
              <>
                <p className="text-sm text-gray-500 text-center">
                  Enter your registered email — we'll send a 6-digit OTP to reset your password.
                </p>
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    autoFocus
                    className="mt-1"
                    data-testid="forgot-email-input"
                  />
                </div>
              </>
            )}

            {forgotStep === 2 && (
              <>
                <p className="text-sm text-gray-500 text-center">
                  We've sent a 6-digit OTP to <span className="font-semibold text-[#1E293B]">{forgotEmail}</span>.
                  Check your inbox (and spam folder).
                </p>
                <div>
                  <Label className="text-sm">OTP</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="123456"
                    value={forgotOtp}
                    onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    autoFocus
                    className="mt-1 text-center text-xl tracking-[0.4em] font-mono"
                    data-testid="forgot-otp-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    setForgotLoading(true);
                    try {
                      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
                      toast.success("New OTP sent");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Failed to resend OTP");
                    } finally {
                      setForgotLoading(false);
                    }
                  }}
                  className="text-xs text-[#FF69B4] hover:underline w-full text-center"
                >
                  Didn't get it? Resend OTP
                </button>
              </>
            )}

            {forgotStep === 3 && (
              <>
                <p className="text-sm text-gray-500 text-center">
                  Choose a new password (at least 6 characters).
                </p>
                <div>
                  <Label className="text-sm">New Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={forgotShowPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={forgotNewPassword}
                      onChange={(e) => setForgotNewPassword(e.target.value)}
                      required
                      autoFocus
                      data-testid="forgot-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setForgotShowPassword(!forgotShowPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {forgotShowPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            <Button
              type="submit"
              disabled={forgotLoading}
              className="w-full py-6 rounded-full text-lg font-semibold bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] text-white hover:opacity-90"
              data-testid="forgot-submit-btn"
            >
              {forgotLoading
                ? "Please wait..."
                : forgotStep === 1
                  ? "Send OTP"
                  : forgotStep === 2
                    ? "Verify OTP"
                    : "Reset Password"}
            </Button>

            {forgotStep > 1 && (
              <button
                type="button"
                onClick={() => setForgotStep(forgotStep - 1)}
                className="text-sm text-gray-500 hover:text-[#7B2FBE] w-full text-center"
              >
                ← Back
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setShowForgotModal(false);
                resetForgotState();
                setShowAuthModal(true);
                setAuthMode("login");
              }}
              className="text-xs text-gray-400 hover:text-[#7B2FBE] w-full text-center"
            >
              Back to Login
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Video Popup */}
      <Dialog open={videoPopup.show} onOpenChange={(open) => setVideoPopup({ ...videoPopup, show: open })}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden">
          <div className="aspect-video bg-black">
            {videoPopup.url && (
              videoPopup.url.includes("youtube") || videoPopup.url.includes("youtu.be") ? (
                <iframe
                  src={`https://www.youtube.com/embed/${videoPopup.url.split("v=")[1]?.split("&")[0] || videoPopup.url.split("/").pop()}`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video src={videoPopup.url} controls className="w-full h-full" autoPlay />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewLandingPage;
